/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import config from '../../../../share/config';
import error from '../../../../common/nodejs/errors';
import util from "../../../../share/util/utils";

/** errorアクション定義 */
const ERR_READ = 'read';
const ERR_WRITE = 'write';
const ERR_EXIT = 'exit';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  SAVE: 'save',
  SUCCESS: 'success',
  RETRY: 'retry',
  NODATA: 'noData',
  ERROR: 'error'
};

export default {
  name: 'requirement002',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      isDisabled: false,
      whichErrApi: '',
      executeRequestId: null,
      readOutData: '',
      saveDate: '',
      saveDataId: '',
      systemId: '',
      readType: '',
      writeType: '',
      isSuccess: false,
      isFirst: true,
      // ダイアログフラグ
      modal_requirement_002: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.START:
        case PHASE.SAVE:
          return 'secondary';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.RETRY:
        case PHASE.NODATA:
        case PHASE.ERROR:
          return 'danger';
        default:
          return 'secondary';
      }
    }
  },

  mounted() {
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('cache', ['cacheVin']),
    ...mapActions('vehicle', ['setVehicleVin']),

    /** 初期化メソッド */
    async init() {
      this.modal_requirement_002 = true;
      this.wsIdToType();
      //EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    //----- 別機能呼び出しメソッド -------------------------------------------//

    // 呼び出された機能別にtypeを設定するメソッド
    wsIdToType(){
      switch(this.workSupportId){
        case 3801:
          this.readType = 'read-ws002';
          this.writeType = 'write-ws002';
        break;
        default:
          this.readType = 'read';
          this.writeType = 'write';
          break;
      }
    },

    // 別機能から呼び出された場合に保存結果返却と遷移するメソッド
    emitResult(){
      if(this.phase === PHASE.SUCCESS && this.workSupportId === 3801){
        this.$emit('isReturn', this.isSuccess);
      }
      else{
        this.$emit('close-wizard');
      }

      this.modal_requirement_002 = false;
    },

     /** 呼び出されたときにモーダルを非表示にするメソッド */
     closeModal() {
      this.modal_requirement_002 = false;
    },

    //----- htmlイベントメソッド -----------------------------------------------//

    /** 読み出し処理を実行するボタンクリックイベント */
    async clickExecute() {
      await this.executePostGet();
    },

    /** 画面を閉じるボタンクリックイベント */
    async clickWizardClose() {
      this.emitResult();
    },

    /** 画面を閉じる（終了処理を実行する）ボタンクリックイベント */
    async clickClose() {
      await this.exitPostGet();
      this.emitResult();
    },

    /** 読み出し処理を実行するボタンクリックイベント */
    async clickRetry() {
      await this.executePostGet();
    },

    /** 保存処理を実行するボタンクリックイベント */
    async clickSave() {
      await this.savePostGet();
    },

    //----- PHASE操作メソッド -----------------------------------------------//

    /** 再読み出し表示メソッド */
    showRetry() {
      this.phase = PHASE.RETRY;
    },

    /** 保存確認表示メソッド */
    showSave() {
      this.phase = PHASE.SAVE;
    },

    /** データなしエラー表示メソッド */
    showNoData() {
      this.phase = PHASE.NODATA;
    },

    /** 保存成功表示メソッド */
    showSuccess() {
      this.phase = PHASE.SUCCESS;
    },

    /** 保存失敗エラー表示メソッド */
    showSaveFailed() {
      this.phase = PHASE.ERROR;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//

    /** データ読み出し（要求）メソッド */
    async executePostGet() {

      try {
        this.isLoading = true;
        this.isDisabled = true;
        this.whichErrApi = ERR_READ;

        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: this.readType,
            is_first : this.isFirst
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          //成功
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            //取得リクエストid設定  ※保存時に使用する
            this.executeRequestId = response.data.user_data.request_id;

            //表示データ編集の共通関数を呼び出す
            const showDat = await individualWorkSupportUtil.getReadOutData(this, this.systemId, response.data.user_data);

            //表示データ編集の共通関数の返却値を設定
            this.readOutData = this.$t(`${this.i18nWsKey}.label_read_data`) + '\n';
            this.readOutData += showDat;

            //表示
            this.showSave();
          }

          //成功以外すべて
          else {
            //サポートデータなしエラー
            if (response.data.async_code === error.WSP_DID_IS_NOT_SUPPORTED_ERROR) {
              this.showNoData();
            }
            //上記以外の失敗
            else {
              this.showRetry();
            }
          }
        }

      } finally {
        this.isLoading = false;
        this.isDisabled = false;
        this.isFirst = false;
      }
    },

    /** 読み出しデータ保存（要求）メソッド */
    async savePostGet() {

      try {
        this.isLoading = true;
        this.isDisabled = true;
        this.whichErrApi = ERR_WRITE;

        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: this.writeType,
            request_id: this.executeRequestId,
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          //成功
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            this.saveDate = util.timeConverter(response.data.user_data.registered_at);
            this.saveDataId = response.data.user_data.data_id;
            this.isSuccess = true;
            this.showSuccess();
          }
          //失敗・ほか
          else {
            this.showSaveFailed();
          }
        }

      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /** 終了処理 (要求) */
    async exitPostGet() {

      try {
        this.isLoading = true;
        this.isDisabled = true;
        this.whichErrApi = ERR_EXIT;

        //バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      switch (this.whichErrApi) {
        case ERR_READ:
          this.showRetry();
          break;
        case ERR_WRITE:
          this.showSaveFailed();
          break;

        //何もしない（非同期により呼び元がexitする）
        case ERR_EXIT:
        default:
          break;
      }
    },

  }
};